import { Button, Grid, IconButton } from '@material-ui/core';
import { CloseIcon, CustomVideoPlayer, PlayIconTwoTone, UploadIconTwoTone } from '@room-match/shared-ui-components';
import { PlayIcon } from 'components/Icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useStyles } from './SingleExpandingContent.styles';
import AuditionSingleComment from './AuditionSingleComment/AuditionSingleComment';
import { IAuditionAttributes } from 'shared/interfaces/IAudition';
import { IAuditionFlag } from 'shared/interfaces/IAuditionFlag';
import { IComment } from 'shared/interfaces/utils/IComment';
import { IMediumAttributes } from 'shared/interfaces/IMedium';
import { MediumTabAction } from 'shared/enums/MediumTabAction';
import { ImageCarousel } from 'components/ImageCarousel';
import {
  getMediaImagesPerGroup,
  getPrimaryImage,
  getScenesPerGroup,
  getSlatesPerGroup,
} from 'shared/utils/auditionMediaAttachments';
import { IAuditionScheduleGroupAttributes } from 'shared/interfaces/IAuditionScheduleGroup';
import { IAuditionScheduleAttributes } from 'shared/interfaces/IAuditionSchedule';
import { IClientPermissionState } from 'shared/redux/slicers/clientPermission.slicer';
import { getMediaId } from 'shared/utils/auditionMediaAttachments';
import { uploadService } from 'shared/services/uploadService';
import { auditionScheduleFlagService } from 'shared/services/auditionScheduleFlagService';
import { getInternalNotesData, getPublicNotesData } from 'shared/utils/auditionFlagParser';
import { useQueryClient } from 'react-query';
import { useGetThumbnail } from 'shared/hooks/useGetThumbnail';

type Props = {
  isEditMode?: boolean;
  audition: IAuditionAttributes;
  auditionSchedule: IAuditionScheduleAttributes;
  groupId: string;
  sessionId?: string;
  clientPermissionsState?: IClientPermissionState;
  handleCloseExpandingContent: () => void;
};

const { getSingleMedia } = uploadService();
const { getAuditionScheduleFlags, createAuditionScheduleFlag } = auditionScheduleFlagService();
const SingleExpandingContent: React.FC<Props> = ({
  isEditMode,
  audition,
  auditionSchedule,
  groupId,
  sessionId,
  clientPermissionsState,
  handleCloseExpandingContent,
}) => {
  const classes = useStyles();

  const [scenes, setScenes] = useState<IMediumAttributes[]>([]);
  const [images, setImages] = useState<IMediumAttributes[]>([]);
  const [slates, setSlates] = useState<IMediumAttributes[]>([]);
  const [selectedAttachments, setSelectedAttachments] = useState<{
    type: string;
    attachments: IMediumAttributes[];
  } | null>(null);

  const { thumbnail, handleChangeVideoUrl, isErrorThumbnail } = useGetThumbnail();

  const queryClient = useQueryClient();
  const { data: auditionFlagData, isLoading: isAuditionFlagLoading } = getAuditionScheduleFlags(auditionSchedule.id, {
    enabled: !!auditionSchedule.id,
  });
  const { mutate: mutateCreateAuditionFlag } = createAuditionScheduleFlag();

  useEffect(() => {
    const getSceneMediums = getScenesPerGroup(auditionSchedule.medium_attachments, groupId);
    const getImagesMediums = getMediaImagesPerGroup(auditionSchedule.medium_attachments, groupId);
    const getSlateMediums = getSlatesPerGroup(auditionSchedule.medium_attachments, groupId);

    setScenes(getSceneMediums);
    setImages(getImagesMediums);
    setSlates(getSlateMediums);

    const getFirstSceneMediums = getSceneMediums[0];
    if (getFirstSceneMediums && getFirstSceneMediums.tag_list.length > 0) {
      setSelectedAttachments({ type: getFirstSceneMediums.tag_list[0], attachments: [getFirstSceneMediums] });
      handleChangeVideoUrl(getFirstSceneMediums.attachment);
      return;
    } else {
      setSelectedAttachments({ type: MediumTabAction.IMAGES, attachments: getImagesMediums });
      handleChangeVideoUrl('');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditionSchedule.medium_attachments]);

  const handleMediumTabActionChange = (mediumTabAction: string, attachments: IMediumAttributes[]) => {
    if (selectedAttachments?.type !== mediumTabAction) {
      setSelectedAttachments({ type: mediumTabAction, attachments });
      if (attachments.length > 0) {
        handleChangeVideoUrl(attachments[0].attachment);
      }
    }
  };

  const onCreateNote = (rating: number, note: string, flagType: 'Public Note' | 'Internal Note' = 'Public Note') => {
    mutateCreateAuditionFlag(
      {
        auditionScheduleId: auditionSchedule.id,
        payload: { flag_type: flagType, comment: note, rating: rating },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['audition_schedule_flag', auditionSchedule.id], {
            refetchInactive: true,
          });
          queryClient.invalidateQueries(['audition'], {
            refetchInactive: true,
          });
        },
      },
    );
  };

  const publicNotes = useMemo(() => {
    return auditionFlagData ? getPublicNotesData(auditionFlagData.data) : [];
  }, [auditionFlagData]);

  const internalNotes = useMemo(() => {
    return auditionFlagData ? getInternalNotesData(auditionFlagData.data) : [];
  }, [auditionFlagData]);

  const publicNotesComments = useMemo(() => {
    return publicNotes.map((publicNote) => {
      return {
        comment: publicNote.attributes.comment,
        rating: publicNote.attributes.rating,
        user: publicNote.attributes.user.data.attributes,
        created_at: publicNote.attributes.created_at,
      };
    });
  }, [publicNotes]) as IComment[];

  const internalNotesComments = useMemo(() => {
    return internalNotes.map((internalNote) => {
      return {
        comment: internalNote.attributes.comment,
        rating: internalNote.attributes.rating,
        user: internalNote.attributes.user.data.attributes,
        created_at: internalNote.attributes.created_at,
      };
    });
  }, [internalNotes]) as IComment[];

  return (
    <div className={classes.expandedDiv}>
      <div className={classes.closeButtonContainer}>
        <IconButton size="small" onClick={() => handleCloseExpandingContent()}>
          <CloseIcon fontSize="small" style={{ fontSize: 14 }} />
        </IconButton>
      </div>
      {/* Content */}
      <Grid container spacing={4}>
        {/* Left Player */}
        <Grid item lg={7}>
          <div className={classes.expandedDiv__leftContainer}>
            <div className={classes.expandedDiv__mediaContainer}>
              {selectedAttachments && (
                <>
                  {selectedAttachments.type.toLocaleLowerCase().startsWith('image') ? (
                    <>
                      {selectedAttachments.attachments.length > 0 && (
                        <ImageCarousel images={selectedAttachments.attachments} />
                      )}
                    </>
                  ) : (
                    <div className={classes.expandedDiv__videoPlayerContainer}>
                      <CustomVideoPlayer
                        url={selectedAttachments.attachments[0].attachment || ''}
                        thumbnail={
                          !isErrorThumbnail
                            ? thumbnail
                            : getPrimaryImage(
                                audition,
                                getMediaImagesPerGroup(auditionSchedule.medium_attachments || [], groupId || ''),
                              )
                        }
                        playIcon={
                          <PlayIcon style={{ fontSize: 48, position: 'absolute', right: '2%', bottom: '3%' }} />
                        }
                        key={Math.random().toString()}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className={classes.expandedDiv__sceneActionContainer}>
              {scenes.map((scene, index) => (
                <Button
                  variant={selectedAttachments?.type !== scene.tag_list[0] ? 'outlined' : 'contained'}
                  color="primary"
                  startIcon={<PlayIconTwoTone classes={{ root: classes.buttonIcon }} />}
                  onClick={() => handleMediumTabActionChange(scene.tag_list[0], [scene])}
                >
                  {`Video ${index + 1}`}
                </Button>
              ))}

              {slates
                .sort((a, b) => a.tag_list[0].localeCompare(b.tag_list[0]))
                .map((slate) => (
                  <Button
                    variant={selectedAttachments?.type !== slate.tag_list[0] ? 'outlined' : 'contained'}
                    color="primary"
                    startIcon={<PlayIconTwoTone classes={{ root: classes.buttonIcon }} />}
                    onClick={() => handleMediumTabActionChange(slate.tag_list[0], [slate])}
                  >
                    {slate.tag_list[0]}
                  </Button>
                ))}
              {images.length > 0 && (
                <Button
                  variant={selectedAttachments?.type !== MediumTabAction.IMAGES ? 'outlined' : 'contained'}
                  color="primary"
                  style={{ color: '#fff' }}
                  startIcon={<UploadIconTwoTone classes={{ root: classes.buttonIcon }} />}
                  onClick={() => handleMediumTabActionChange(MediumTabAction.IMAGES, images)}
                >
                  Images
                </Button>
              )}
            </div>

            {/* Scene Action */}
            {/* Actions */}
          </div>
        </Grid>
        {/* Comments Section */}
        <Grid item lg={5}>
          <AuditionSingleComment
            isEditMode={isEditMode}
            publicNotesComments={publicNotesComments}
            internalNotesComments={internalNotesComments}
            onCreateNote={onCreateNote}
            auditionSchedule={auditionSchedule}
            audition={audition}
            groupId={groupId}
            sessionId={sessionId || ''}
            isDisabledComment={
              clientPermissionsState ? !clientPermissionsState?.projectPermissions?.add_comments_ratings : false
            }
            isDisabledCallback={
              clientPermissionsState ? !clientPermissionsState?.projectPermissions?.select_callback : false
            }
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default SingleExpandingContent;
